import type { AnalyticsCaptureRequest } from "@meta-video/contracts";
import { useCallback, useMemo } from "react";

import { useAnalytics, useAnalyticsSessionId } from "./AnalyticsProvider.js";
import { analyticsEvents } from "./events.js";

type Properties<Name extends AnalyticsCaptureRequest["event"]> = Extract<
  AnalyticsCaptureRequest,
  { readonly event: Name }
>["properties"];
type SavedViewProperties = Properties<"saved view action completed">;
type AdminProperties = Properties<"admin action completed">;

export function useProductAnalytics() {
  const analytics = useAnalytics();
  const sessionId = useAnalyticsSessionId();

  const reportInteraction = useCallback((
    properties: Properties<"report interaction completed">,
  ) => {
    if (!sessionId) return;
    analytics.capture(analyticsEvents.reportInteraction(sessionId, properties));
  }, [analytics, sessionId]);

  const savedViewAction = useCallback((
    action: SavedViewProperties["action"],
    count?: number,
  ) => {
    if (!sessionId) return;
    analytics.capture(
      analyticsEvents.savedViewAction(sessionId, {
        action,
        ...(count === undefined ? {} : { count }),
        status: "success",
      }),
    );
  }, [analytics, sessionId]);

  const adminAction = useCallback((
    properties: Omit<AdminProperties, "status">,
  ) => {
    if (!sessionId) return;
    analytics.capture(
      analyticsEvents.adminAction(sessionId, {
        ...properties,
        status: "success",
      }),
    );
  }, [analytics, sessionId]);

  return useMemo(() => ({
    adminAction,
    reportInteraction,
    savedViewAction,
  }), [adminAction, reportInteraction, savedViewAction]);
}
